import {Injectable} from '@angular/core';
import {connectApi} from './connect-api';
import {map} from 'rxjs/internal/operators';
import {NotificationPreferenceType, User} from './user';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {Login} from '../login/login';
import {SESSION_STORAGE_KEYS} from "./session-storage-keys";

export const PasswordRegexConstant = {
    ALLOWED_CHARS_REGEX : /^[A-Za-z0-9!"#\$%&'\(\)\*\+,\-\./:;<=>\?@\[\\\]\^_`\{\|\}~]{8,}$/,
    ONE_UPPER_CASE_REGEX : /[A-Z]+/,
    ONE_NUMBER_CASE_REGEX : /\d+/,
    ONE_SPECIAL_REGEX : /[!"#\$%&'\(\)\*\+,\-\./:;<=>\?@\[\\\]\^_`\{\|\}~]/,
    CONSEC_CHAR_CHECK_REGEX : /(.)\1{2,}/

}
export const passwordErrorMessages = {
    'pwd8CharacterLimit' : "Password must be at least 8 characters in length or greater",
    'pwdSpecialCharacter' : "No spaces allowed. Allowed characters are: A to Z, a to z, 0 to 9 and special characters",
    'pwd1UpperCaseLetter' : "Password must have at least one uppercase letter",
    'pwd1Number' : "Password must have at least one number",
    'pwd1SpecialCharacter' : "Password must have at least one special character",
    'pwd2SameCharacter' : "Password cannot have more than 2 consecutive characters being the same letter, number or special character",
    'pwdSamePassword' : "Confirm Password must match New Password"
}

export const passwordSuccessMessages = {
    'resetLinkSent' : 'A Password Reset link has been sent to your email account.',
    'resetLinkSent2' : 'Follow the instructions in the email to complete your password reset.'
}


@Injectable({
    providedIn: 'root'
})
export class UserAuthenticationService {
    constructor(private http: HttpClient) {
    }

    verifyPassword(pwd : string) : string[] {

        let pwdErrorMessages = [];


        let allowedChars : RegExp = PasswordRegexConstant.ALLOWED_CHARS_REGEX;
        let oneUppercase : RegExp = PasswordRegexConstant.ONE_UPPER_CASE_REGEX;
        let oneNumber : RegExp = PasswordRegexConstant.ONE_NUMBER_CASE_REGEX;
        let oneSpecial : RegExp = PasswordRegexConstant.ONE_SPECIAL_REGEX;
        let consecChars : RegExp = PasswordRegexConstant.CONSEC_CHAR_CHECK_REGEX;

        if(pwd.length < 8) {
            pwdErrorMessages.push(passwordErrorMessages.pwd8CharacterLimit);
        }

        if(!allowedChars.test(pwd)) {
            pwdErrorMessages.push(passwordErrorMessages.pwdSpecialCharacter);
        }

        if(!oneUppercase.test(pwd)) {
            pwdErrorMessages.push(passwordErrorMessages.pwd1UpperCaseLetter);
        }

        if(!oneNumber.test(pwd)) {
            pwdErrorMessages.push(passwordErrorMessages.pwd1Number);
        }

        if(!oneSpecial.test(pwd)) {
            pwdErrorMessages.push(passwordErrorMessages.pwd1SpecialCharacter);
        }

        if(consecChars.test(pwd)) {
            pwdErrorMessages.push(passwordErrorMessages.pwd2SameCharacter);
        }

        return pwdErrorMessages;
    }

    resetPassword(user: User): Observable<User> {
        return this.http
            .put(connectApi.resetPassword, JSON.stringify(user))
            .pipe(map(res => new User( res['User'])));
    }

    updateUserPwd(user: User): Observable<User> {
        return this.http
            .put(connectApi.credentials, JSON.stringify(user))
            .pipe(map(res => new User( res['User'])));
    }

    requestPasswordResetLink(email): Observable<User> {
        let data = {
            "email": email,
            "loginId": email
        };
        return this.http
            .post(connectApi.forgotPassword, JSON.stringify(data))
            .pipe(map(res => new User( res['User'])));
    }


    getUserByToken(token: string): Observable<User> {
        return this.http
            .post(`${connectApi.getUserByToken}/${token}`, undefined)
            .pipe(map(res => new User(res['User'])));
    }

    registerUser(login: Login): Observable<User> {
        return this.http
            .post(connectApi.register, login )
            .pipe(map(res => new User( res['User'])));
    }

    login(login: Login): Observable<User> {
        return this.http
            .post(connectApi.login, login)
            .pipe(map(res => new User( res['User'])));
    }

    setUpUserSession(user: User) : void {
        if (user) {
            sessionStorage.setItem(SESSION_STORAGE_KEYS.user, JSON.stringify(user));
            sessionStorage.setItem(SESSION_STORAGE_KEYS.userId, user.id.toString());
            if (user.customerAccountId) {
                sessionStorage.setItem(SESSION_STORAGE_KEYS.customerAccountId, user.customerAccountId.toString());

            }
        }
    }

    updateNotificationPreference(notificationPreferenceType: NotificationPreferenceType): Observable<User> {
        let url = connectApi.updateNotificationPreference.replace('{notificationPreferenceType}', notificationPreferenceType)
        return this.http
            .put(url, null)
            .pipe(map(res => new User( res['User'])));
    }

    updateLoginParams(verifyRequest: Login) : Observable<any> {
        return this.http
            .post(connectApi.updateLoginParams, verifyRequest)
            .pipe(map(res => res));
    }

    updateUserEmail(user : User ) : Observable<User> {
        return this.http
            .put(connectApi.loginId, JSON.stringify(user))
            .pipe(map(res => new User( res['User'])));
    }

}
